import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import { MonsterSpriteGenerator } from "../assets/MonsterSpriteGenerator";
import { SeededRandom } from "../core/SeededRandom";
import type { GridMap } from "../generation/GridMap";
import type { EnemySpawn } from "../generation/types";
import type { MaterialFactory } from "../rendering/MaterialFactory";
import type { AudioSystem } from "../systems/AudioSystem";
import type { CircleCollisionBody } from "../systems/CollisionSystem";
import { Enemy, type EnemyCallbacks } from "./Enemy";

export class EnemySpawner {
  public readonly enemies: readonly Enemy[];

  public constructor(
    spawns: readonly EnemySpawn[],
    seed: string,
    scene: Scene,
    grid: GridMap,
    materials: MaterialFactory,
    audio: AudioSystem,
    callbacks: Omit<EnemyCallbacks, "alertAllies">,
  ) {
    const sprites = new MonsterSpriteGenerator(seed);
    const enemies: Enemy[] = [];
    const fullCallbacks: EnemyCallbacks = {
      ...callbacks,
      alertAllies: (source, position) => this.alertAllies(enemies, source, position),
    };
    for (const spawn of spawns) {
      enemies.push(
        new Enemy(
          spawn,
          scene,
          grid,
          materials,
          sprites,
          new SeededRandom(`${seed}:enemy:${spawn.id}`),
          audio,
          fullCallbacks,
        ),
      );
    }
    this.enemies = enemies;
  }

  public update(deltaSeconds: number, playerPosition: Vector3, grid: GridMap): void {
    for (const enemy of this.enemies) enemy.update(deltaSeconds, playerPosition, grid);
  }

  public broadcastShot(position: Vector3): void {
    for (const enemy of this.enemies) enemy.hear(position);
  }

  public getById(id: string): Enemy | undefined {
    return this.enemies.find((enemy) => enemy.spawn.id === id);
  }

  public collectLivingPositions(target: Vector3[]): void {
    for (const enemy of this.enemies) {
      if (!enemy.dead) target.push(enemy.position);
    }
  }

  public collectLivingCollisionBodies(target: CircleCollisionBody[]): void {
    target.length = 0;
    for (const enemy of this.enemies) {
      if (!enemy.dead) target.push(enemy.collisionBody);
    }
  }

  private alertAllies(enemies: readonly Enemy[], source: Enemy, position: Vector3): void {
    for (const enemy of enemies) {
      if (
        enemy !== source &&
        !enemy.dead &&
        Vector3.Distance(enemy.position, source.position) <= 11
      ) {
        enemy.alertFromAlly(position);
      }
    }
  }
}
